import  React from "react";
import type { FC } from "react";

import { useIntl } from "react-intl";

import { useRecoilCallback, useRecoilValue } from "recoil";

import {
	BUTTON_VARIANT_DANGER,
	BUTTON_VARIANT_SECONDARY,
	Button,
	Flex,
	LINK_VARIANT_EXTRALIGHT,
	Link,
	Modal,
	ModalBody,
	ModalFoot,
	ModalHead,
	RadioButton,
	SPACING_4,
	SpacingVertical,
	TEXT_VARIANT_LIGHT,
	TEXT_VARIANT_STANDARD,
	Text,
} from "mailup-web-components";

import {
	modalDeleteCancelCallback,
	modalDeleteChangeCallback,
	modalDeleteConfirmCallback,
} from "Pages/RecipientsList/recoil/callbacks/modals";
import {
	modalDeleteCanConfirmState,
	modalDeleteState,
} from "Pages/RecipientsList/recoil/state/modals";

import { gainsightTrack } from "mailup-react-helpers";

const ModalDelete: FC = () => {
	const intl = useIntl();

	const { show, mode } = useRecoilValue(modalDeleteState);

	const canConfirm = useRecoilValue(modalDeleteCanConfirmState);

	const handleCancel = useRecoilCallback(modalDeleteCancelCallback, []);
	const handleConfirm = useRecoilCallback(modalDeleteConfirmCallback, []);

	const changeMode = useRecoilCallback(modalDeleteChangeCallback, []);

	const handleDeleteChange = (
		_evt: React.ChangeEvent<HTMLInputElement>,
	): void => {
		gainsightTrack("mup_recipients_click_modalDeleteDeleteRadio", {});
		void changeMode("delete");
	};

	const handleUnsubscribeChange = (
		_evt: React.ChangeEvent<HTMLInputElement>,
	): void => {
		gainsightTrack("mup_recipients_click_modalDeleteUnsubscribeRadio", {});
		void changeMode("unsubscribe");
	};

	return (
		<Modal
			show={show}
			onClose={handleCancel}
			data-mu-test="modal-delete"
			closeOnOverlayClick
			showClose
		>
			<ModalHead
				title={intl.formatMessage({
					id: "Load.RecipientsList.ModalDeleteTitle",
				})}
			/>

			<ModalBody>
				<Flex flexDirection="column" gap="24px">
					<Text variant={TEXT_VARIANT_STANDARD}>
						{intl.formatMessage({
							id: "Load.RecipientsList.ModalDeleteIntro",
						})}
					</Text>

					<article>
						<RadioButton
							data-mu-test="option-delete"
							checked={mode === "delete"}
							onChange={handleDeleteChange}
							label={
								<>
									{intl.formatMessage({
										id: "Load.RecipientsList.ActionPermanentDelete",
									})}{" "}
									(
									{intl.formatMessage({
										id: "Load.Common.NotRecommendedLowercase",
									})}
									)
								</>
							}
						/>

						<SpacingVertical variant={SPACING_4} />

						<Flex flexDirection="row" gap="26px">
							<i />
							<Text variant={TEXT_VARIANT_LIGHT}>
								{intl.formatMessage({
									id: "Load.RecipientsList.PermanentDeleteDescription",
								})}
							</Text>
						</Flex>
					</article>

					<article>
						<RadioButton
							data-mu-test="option-unsubscribe"
							checked={mode === "unsubscribe"}
							onChange={handleUnsubscribeChange}
							label={intl.formatMessage({
								id: "Load.RecipientsList.ActionUnsubscribe",
							})}
						/>

						<SpacingVertical variant={SPACING_4} />

						<Flex flexDirection="row" gap="26px">
							<i />
							<Text variant={TEXT_VARIANT_LIGHT}>
								{intl.formatMessage({
									id: "Load.RecipientsList.ActionUnsubscribeDescription",
								})}
							</Text>
						</Flex>
					</article>
				</Flex>
			</ModalBody>
			<ModalFoot>
				<Link
					variant={LINK_VARIANT_EXTRALIGHT}
					data-mu-test="cancel"
					onClick={handleCancel}
				>
					{intl.formatMessage({ id: "Load.MailupCommon.Cancel" })}
				</Link>
				<Button
					kind={
						mode === "unsubscribe"
							? BUTTON_VARIANT_SECONDARY
							: BUTTON_VARIANT_DANGER
					}
					data-mu-test="confirm"
					onClick={handleConfirm}
					disabled={!canConfirm}
				>
					{intl.formatMessage({
						id:
							mode === "unsubscribe"
								? "Load.RecipientsList.ActionUnsubscribe"
								: "Load.RecipientsList.ActionPermanentDelete",
					})}
				</Button>
			</ModalFoot>
		</Modal>
	);
};

export default ModalDelete;
